import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ConversationContext,
  GroundedSummaryTextFragment,
  PatientSummaryByContextNavigationRecommendation,
  PatientSummaryData,
  PolicyRecommendation,
  PossibleCauses,
  SummaryOptions,
  SummaryTextFragment,
  WorkupActionItems,
  WorkupItem,
  Summary,
  SummaryDiseaseCluster,
  SummaryProblem,
} from "fe-common/graphql";
import { useTranslateByGender } from "../locale/useTranslateByGender";
import { CaseStatus } from "@kahunmedical/common-wtypes";
import useTimeout from "../hooks/useTimeout";

export interface UsePatientSummaryApi {
  summary: PatientSummaryData;
  recommendations?: any[];
  possibleCause?: SummaryDiseaseCluster[];
  possibleCauses?: PossibleCauses;
  navigationAdvice?: PolicyRecommendation;
  problemList?: SummaryProblem[];
  assessment?: GroundedSummaryTextFragment;
  workup?: SummaryTextFragment;
  workupList?: WorkupItem[];
  workupActionItems?: WorkupActionItems;
  loading?: boolean;
  polling?: PollingSections;
  error?: string;
  showIcdCodes?: boolean;
  getSummary?: (etag?: string) => void;
  caseEtag?: string;
  caseStatus?: string;
  ddReady?: boolean;
  assessmentGroundingReady?: boolean;
}

export type PollingSections = {
  dd: boolean;
  assessment: boolean;
  workup: boolean;
  groundingAssessment: boolean;
  groundingExplanation: boolean;
};

export const inProgress = (status: string) => {
  return (
    !!status &&
    status !== CaseStatus.COMPLETED &&
    status !== CaseStatus.ABANDONED
  );
};

export function usePatientSummary(
  caseId: string,
  clinicId: string,
  lang?: string,
  showIcdCodes: boolean = false,
  useLLM?: boolean,
  useDialogueForInsights?: boolean,
  groundAssessment?: boolean,
  groundExplanation?: boolean,
  options?: SummaryOptions,
): UsePatientSummaryApi {
  const interval = 4000;
  const [data, setData] = useState<Summary>(null);
  const dao = useDaoContext();
  const [error, setError] = useState<string>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [polling, setPolling] = useState<PollingSections>(null);
  const [ddReady, setDdReady] = useState<boolean>(false); //this flag is an indication for the *walkthrough* to begin
  const [assessmentGroundingReady, setAssessmentGroundingReady] = useState<
    boolean
  >(false); //this flag is an indication for the *walkthrough* to begin from grounding

  const { translate } = useTranslateByGender();

  const pollingTimeout = useTimeout(() => {
    getData();
  }, interval);

  const stopPolling = () => {
    setLoading(false);
    setPolling(null);
  };

  const getData = useCallback(
    async (etag?: string) => {
      function isLatest(
        obj: {
          etag?: string;
          [otherOptions: string]: unknown;
        },
        caseEtag: string,
      ) {
        return obj && obj.etag === caseEtag && !obj.generating;
      }
      setData(null);

      setError(null);

      try {
        const result = await dao.getPatientFirstSummary(
          caseId,
          clinicId,
          { ...options, showIcdCodes },
          false,
          lang,
        );

        //backwards compatibility for old workup list
        const updated = { ...result };
        if (
          !updated.workupActionItems?.items?.length &&
          updated.workupList?.length
        ) {
          updated.workupActionItems.items = [...updated.workupList];
        }
        //--------------------------------------
        if (!etag || result.caseEtag === etag) setData(updated as Summary);

        if (useLLM && !useDialogueForInsights) {
          const {
            caseEtag,
            validCase,
            status,
            possibleCauses,
            assessment,
            workupActionItems,
          } = result;

          //check case validity
          if (isLatest(validCase, caseEtag) && !validCase.isValid) {
            console.log(`invalid case: ${validCase.reason}`);
            stopPolling();
            setError(validCase.reason);
            return null;
          }

          const awaitOfflineResults = groundAssessment || groundExplanation; //should polling wait for offline results

          if (
            awaitOfflineResults &&
            (inProgress(status) || (!!etag && caseEtag !== etag))
          ) {
            setPolling({
              dd: !isLatest(possibleCauses, caseEtag),
              assessment: !isLatest(assessment, caseEtag),
              workup: !isLatest(workupActionItems, caseEtag),
              groundingAssessment:
                groundAssessment && !isLatest(assessment?.grounding, caseEtag),
              groundingExplanation:
                groundExplanation &&
                possibleCauses?.possibleCauses?.some(
                  (pc) => !isLatest(pc.explanation?.grounding, caseEtag),
                ),
            });
            setDdReady(isLatest(possibleCauses, caseEtag));
            setAssessmentGroundingReady(
              isLatest(assessment?.grounding, caseEtag),
            );
            pollingTimeout(caseEtag);
          } else {
            //localStorage.removeItem("caseUpdate");
            setDdReady(true);
            setAssessmentGroundingReady(true);
            stopPolling();
          }
        } else {
          stopPolling();
        }

        // attempts++;
        // if (attempts > maxAttempts) {
        //   console.log(`Exceeded max attempts (${maxAttempts})`);
        //   stopPolling();
        //   setLoading(false);
        //   if (!workup?.text) setPollingError("Summary not received."); //workup.text appears if workupList is not present
        //   setAssessmentTag(assessment?.etag || "-1");
        //   setWorkupTag(workup?.etag || "-1");
        //   return null;
        // }
      } catch (e) {
        console.error("error getting summary:", e);
        stopPolling();
        setError(translate("SUMMARY_ERROR"));
      }
    },
    [caseId, clinicId, dao, lang, showIcdCodes, useLLM],
  );

  const getSummary = (etag?: string) => {
    //start polling
    setLoading(true);
    setPolling({
      dd: !useDialogueForInsights,
      assessment: !useDialogueForInsights,
      workup: !useDialogueForInsights,
      groundingAssessment: groundAssessment,
      groundingExplanation: groundExplanation,
    });

    getData(etag);
  };

  useEffect(() => {
    setData(null);
    if (caseId) getSummary();
    return () => stopPolling(); //cleanup
  }, [caseId]);

  const {
    data: summary,
    possibleCauses,
    assessment,
    workupActionItems,
    caseEtag,
    workup,
    status: caseStatus,
  } = data || {};

  return {
    summary,
    possibleCauses,
    assessment,
    workup,
    workupActionItems,
    caseEtag,
    caseStatus,
    error,
    loading,
    polling,
    ddReady,
    assessmentGroundingReady,
    getSummary,
  };
}

export function usePatientSummaryByContext(
  context: ConversationContext,
  options: SummaryOptions,
): UsePatientSummaryApi {
  const [summary, setSummary] = useState<PatientSummaryData>(null);

  const [navigation, setNavigation] = useState<
    PatientSummaryByContextNavigationRecommendation
  >(null);

  const [loading, setLoading] = useState<boolean>(true);

  const dao = useDaoContext();

  const sentPatientSummary = useRef<string>(null);

  useEffect(() => {
    sentPatientSummary.current = null;
  }, [context]);

  useEffect(() => {
    if (!context) return;
    (async () => {
      if (!sentPatientSummary.current) {
        sentPatientSummary.current = context;
        setLoading(true);
        const {
          summary,
          navigationRecommendation,
        } = await dao.getPatientFirstSummaryByContext(context, options);
        setSummary(summary);
        setNavigation(navigationRecommendation);
        setLoading(false);
      }
    })();
  }, [context, dao]);

  return {
    summary,
    recommendations: [],
    navigationAdvice: navigation,
    loading,
  };
}
